/** @format */

import {
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type CellContext,
  type Row,
  type RowSelectionState,
  type Table,
  type VisibilityState,
} from '@tanstack/react-table'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react'

import {Checkbox} from '@src/components/tailwind/CheckboxV2'
import {StatusIndicator} from '@src/components/tailwind/StatusIndicator'

import {useAccountContext, useFormApi} from '@src/contexts'
import type {DisplayConfig} from '@src/types/Form'

import {
  ALWAYS_HIDDEN_FIELDS,
  ALWAYS_VISIBLE_FIELDS,
  EMPTY_KEY,
  MS_PER_DAY,
  RESERVED_FIELDS,
} from './constants'
import {useSubmissionsContext, type Submission} from './SubmissionsContext'
import {getStatusIndicatorType, isOverlimitSubmission} from './utils'

type TableModel = {
  clearRowSelection: () => void
  columnOrder: string[]
  columnVisibility: VisibilityState
  rowSelection: RowSelectionState
  selectAllMatching: boolean
  setColumnOrder: (columnOrder: string[]) => void
  setColumnVisibility: (columnVisibility: VisibilityState) => void
  setSelectAllMatching: (selectAllMatching: boolean) => void
  table: Table<Submission>
}

const TableModelContext = createContext<TableModel | null>(null)

const columnHelper = createColumnHelper<Submission>()

export function TableModelProvider(props: PropsWithChildren) {
  const {children} = props

  const {current: currentForm} = useFormApi()
  const {data, filter, pagination, setPagination} = useSubmissionsContext()

  const [columnOrder, setColumnOrder] = useState(() => {
    return getInitialColumnOrder(currentForm.display_config, data.fields)
  })

  const [columnVisibility, setColumnVisibility] = useState(() => {
    return getInitialColumnVisibility(currentForm.display_config, data.fields)
  })

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  // Whether the selection extends to every matching submission across pages
  // (Gmail-style "Select all N"), not just the loaded rows.
  const [selectAllMatching, setSelectAllMatching] = useState(false)
  const clearRowSelection = useCallback(() => {
    setRowSelection({})
    setSelectAllMatching(false)
  }, [])

  const columns = useMemo(() => {
    const fields = data.fields.filter(f => !RESERVED_FIELDS.has(f))
    const sizes = currentForm.display_config?.sizes ?? {}
    const columns = [
      columnHelper.display({
        cell: ({row}) => (
          <Checkbox
            checked={row.getIsSelected()}
            // past-retention submissions can't be acted on
            disabled={!row.getCanSelect()}
            onCheckedChange={row.getToggleSelectedHandler()}
            // Don't expand row when clicking on the checkbox
            onClick={event => event.stopPropagation()}
          />
        ),
        enableHiding: false,
        enableResizing: false,
        header: ({table}) => {
          // `getIsSomeRowsSelected` counts every row, selectable or not, so it
          // stays true once the unselectable past-limit rows are on the page.
          // Check for "all selectable rows selected" first.
          const checked = table.getIsAllRowsSelected()
            ? true
            : table.getIsSomeRowsSelected()
              ? 'indeterminate'
              : false
          return (
            <Checkbox
              checked={checked}
              onCheckedChange={(checked: boolean) =>
                table.toggleAllRowsSelected(checked)
              }
            />
          )
        },
        id: '_selection',
      }),
      columnHelper.accessor('_status', {
        cell: info => {
          const value = info.getValue()
          const type = getStatusIndicatorType(value)
          return type ? (
            <StatusIndicator className="h-5 w-5" type={type} />
          ) : null
        },
        enableResizing: false,
        header: () => <span className="sr-only">_status</span>,
      }),
      columnHelper.accessor('_date', {
        enableHiding: false,
        size: sizes._date,
      }),
    ]
    for (const field of fields) {
      const cell =
        field === EMPTY_KEY
          ? (info: CellContext<Submission, unknown>) => info.row.original['']
          : (info: CellContext<Submission, unknown>) => info.getValue()

      const size = sizes[field]
      const c = columnHelper.accessor(
        field,
        // Don't pass `size: undefined`; otherwise, the table will
        // fall back to the library's default size (150) instead of
        // our intended default (200).
        size ? {cell, size} : {cell},
      )
      columns.push(c)
    }
    return columns
  }, [currentForm.display_config?.sizes, data.fields])

  // The API returns `total_pages: -1` when the number of submissions
  // exceeds `SUBMISSION_QUERY_LIMIT`.
  const pageCount =
    data.total_pages === -1 ? Number.MAX_SAFE_INTEGER : data.total_pages

  // Submissions past the retention limit can be neither opened nor acted on.
  const isRowAccessible = useIsRowAccessible()

  const table = useReactTable({
    columns,
    columnResizeMode: 'onChange',
    data: data.submissions,
    defaultColumn: {
      size: 200,
      maxSize: 960,
      minSize: 48,
    },
    enableRowSelection: isRowAccessible,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    // an over-limit row arrives with every column cut short, so there is
    // nothing to open; it can still be selected for Reprocess and Delete
    getRowCanExpand: row =>
      isRowAccessible(row) && !isOverlimitSubmission(row.original._status),
    getRowId: row => `${row._id}`,
    manualPagination: true,
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    onRowSelectionChange: updater => {
      // any manual (de)selection invalidates the cross-page selection
      setSelectAllMatching(false)
      setRowSelection(updater)
    },
    pageCount,
    state: {
      columnOrder,
      columnVisibility,
      pagination,
      rowSelection,
    },
  })

  // Reset table states when the folder filter changes.
  //
  // We "key" this effect by a specific property of `filter` (e.g., `filter.folder`)
  // rather than the entire `filter` object, as it helps avoid
  // unnecessary resets from unrelated filter changes.
  useEffect(() => {
    setRowSelection({})
    setSelectAllMatching(false)
    setPagination(current => ({...current, pageIndex: 0}))
  }, [filter.folder, setPagination])

  const model = {
    clearRowSelection,
    columnOrder,
    columnVisibility,
    rowSelection,
    selectAllMatching,
    setColumnOrder,
    setColumnVisibility,
    setSelectAllMatching,
    table,
  } satisfies TableModel

  return (
    <TableModelContext.Provider value={model}>
      {children}
    </TableModelContext.Provider>
  )
}

export function useTableModel(): TableModel {
  const model = useContext(TableModelContext)
  if (!model) {
    throw new Error('useTableModel must be used in TableModelProvider.')
  }
  return model
}

/**
 * Internal
 */

function getInitialColumnOrder(
  displayConfig: DisplayConfig | null,
  fields: string[],
): string[] {
  // Show all fields if the custom order was not previously set
  const saved = displayConfig?.order ?? fields
  return [
    '_selection',
    '_status',
    '_date',
    ...saved.filter(f => !RESERVED_FIELDS.has(f)),
  ]
}

function getInitialColumnVisibility(
  displayConfig: DisplayConfig | null,
  fields: string[],
): Record<string, boolean> {
  const allFields = new Set(
    fields.filter(field => !ALWAYS_HIDDEN_FIELDS.has(field)),
  )
  // Ensure always visible fields are included, even if not in fields or displayConfig (e.g. _selection)
  for (const f of ALWAYS_VISIBLE_FIELDS) {
    allFields.add(f)
  }

  const selected = new Set(displayConfig?.order)

  // Show all fields if there is no saved order
  if (selected.size === 0) {
    return Object.fromEntries([...allFields].map(field => [field, true]))
  }

  for (const f of ALWAYS_VISIBLE_FIELDS) {
    selected.add(f)
  }
  return Object.fromEntries([...allFields].map(f => [f, selected.has(f)]))
}

// Whether a submission is within the plan's retention window, and so can be
// opened and selected. Drives both `getRowCanExpand` and `enableRowSelection`.
//
// `hasExtra` is the server's word on whether this response carries anything
// past the limit. When it says no, every row is accessible without consulting
// the browser clock at all.
function isRowAccessible(
  retention: {infinite: boolean; limit: number},
  hasExtra: boolean,
  date: string,
): boolean {
  if (retention.infinite || !hasExtra) {
    return true
  }
  // Compare against the retention window rather than the page index:
  // over-limit submissions can spill onto a page other than the last one.
  const retentionDate = Date.now() - retention.limit * MS_PER_DAY
  return new Date(date).getTime() >= retentionDate
}

function useIsRowAccessible(): (row: Row<Submission>) => boolean {
  const {account} = useAccountContext()
  const {data} = useSubmissionsContext()
  const {infinite, limit} = account.limits.retention
  const hasExtra = data.has_extra
  return useCallback(
    row => isRowAccessible({infinite, limit}, hasExtra, row.original._date),
    [infinite, limit, hasExtra],
  )
}

export const __testingOnly__ = {
  getInitialColumnOrder,
  getInitialColumnVisibility,
  isRowAccessible,
}
